import { lazy, Suspense, useEffect, useState } from "react";
import { HelpCircle } from "lucide-react";

// Lazy — HelpDrawer ciągnie react-markdown + remark-gfm (~100 kB gz),
// a FAB jest na każdej stronie. Chunk ładuje się przy pierwszym otwarciu;
// drawer i tak zwraca null gdy zamknięty, więc gating na `open` niczego
// nie zmienia w zachowaniu.
const HelpDrawer = lazy(() => import("./HelpDrawer"));

/**
 * Pływający przycisk pomocy (FAB) — zawsze widoczny w prawym dolnym rogu,
 * z-index 8980 (poniżej drawer-a 9000, ale nad mapą 1000+).
 *
 * Otwiera drawer pomocy. Skrót klawiszowy `?` (Shift+/) toggluje stan.
 * Tooltip „Pomoc" / „Help" zależnie od wybranego języka.
 *
 * Renderowany w RootRoute — widoczny na każdej stronie aplikacji,
 * zarówno publicznej (mieszkaniec) jak i protected (admin/konsultant).
 */
export default function HelpButton() {
  const [open, setOpen] = useState(false);

  // Skrót `?` (Shift+/) — toggle drawer-a. Ignorujemy gdy focus jest
  // w polu input/textarea/contentEditable żeby nie blokować pisania.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "?" || !e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if (isTyping) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Pomoc / Help (?)"
        aria-label="Otwórz pomoc"
        className="fixed bottom-4 right-4 z-[8980] h-12 w-12 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all flex items-center justify-center group">
        <HelpCircle className="h-6 w-6" />
        <span className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Pomoc · ?
        </span>
      </button>
      {open && (
        <Suspense fallback={null}>
          <HelpDrawer open={open} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
